import React, { FC, useCallback, useMemo, CSSProperties, ReactNode } from "react";
import Dropzone from "react-dropzone";
import theme from "styles/theme";
import filesize from "filesize";
import Promise from "bluebird";
import { format } from "lib/globalize-util";
import { ContentLoading } from "components/content-loading";
import { ValidationError } from "components/upload/validation-error";

export interface IUploaderProps {
    accept: string; //All MIME types that this uploader will accept, eg. "image/jpeg, image/png",
    onDrop: (files: File[]) => void;
    onDropReject?: (rejected: File[], accepted: File[]) => void;
    onUploadError: (error: ValidationError) => void;
    defaultError?: string;
    inputProps?: { [key: string]: any };
}

export interface IValidationProps {
    minFiles?: number;
    maxFiles?: number;
    maxSize?: number;
    minSize?: number;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
}

interface IAspectRatioProps {
    aspectRatio?: "square" | "custom";
    aspectRatioH?: number;
    aspectRatioV?: number;
}

interface IProps extends IUploaderProps, IValidationProps, IAspectRatioProps {
    loading?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    children?: ReactNode;
    style?: CSSProperties;
    activeStyle?: CSSProperties;
    rejectStyle?: CSSProperties;
    dataTest?: string;
    maxSizeError?: string;
}

export const DropzoneUploader: FC<IProps> = (props) => {
    const {
        children,
        multiple,
        accept,
        loading,
        maxSize,
        minSize,
        defaultError,
        disabled = loading,
        inputProps,
        dataTest,
        maxSizeError,
    } = props;
    const { onDrop, onDropReject, onUploadError } = props;

    const handleDropReject = useCallback(
        (files: File[], accepted: File[]) => {
            if (!multiple && files.length + accepted.length > 1) {
                onUploadError(
                    new ValidationError(format("Please upload no more than |n| files at a time", { n: 1 }), "multiple")
                );
            } else if (maxSize && files[0].size > maxSize) {
                onUploadError(
                    new ValidationError(
                        maxSizeError || format("Max file size |size|", { size: filesize(maxSize) }),
                        "maxSize"
                    )
                );
            } else {
                onUploadError(
                    new ValidationError(
                        defaultError || format("Invalid file(s), please ensure your files meet the requirements.")
                    )
                );
            }

            if (onDropReject) {
                onDropReject(files, accepted);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [maxSize, defaultError, onDropReject, onUploadError]
    );

    const handleDrop = useCallback(
        (files, rejected) => {
            if (files.length && rejected.length === 0) {
                validateCount(files, props)
                    .map((fileData) => validateDimensions(fileData, props), { concurrency: 6 })
                    .then(() => onDrop(files))
                    .catch((err) => onUploadError(err));
            } else if (rejected.length) {
                handleDropReject(rejected, files);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onDrop, onUploadError, handleDropReject]
    );

    const dropzoneStyles = withBaseStyles(props);

    return (
        <Dropzone
            multiple={multiple}
            onDrop={handleDrop}
            accept={accept}
            disabled={disabled}
            maxSize={maxSize}
            minSize={minSize}
            inputProps={inputProps}
            {...dropzoneStyles}
            data-test={dataTest}
        >
            {children}
            {loading && <ContentLoading />}
        </Dropzone>
    );
};

export const validateDimensions = (fileData: File, specs: IValidationProps & IAspectRatioProps): Promise<File> => {
    const { minWidth, maxWidth, minHeight, maxHeight, aspectRatio, aspectRatioH, aspectRatioV } = specs;
    // check dimensions for images only
    if (
        !["image/jpeg", "image/png"].includes(fileData.type) ||
        !(minWidth || maxWidth || minHeight || maxHeight || aspectRatio)
    ) {
        return Promise.resolve(fileData);
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(fileData);
        reader.onload = (event) => {
            const image = new Image();
            (image as any).src = event.target.result;
            image.onload = () => {
                const width = image.width;
                const height = image.height;

                if (minWidth && width < minWidth) {
                    return reject(
                        new ValidationError(
                            format("Error Uploading: Minimum width is |minWidth|px", { minWidth }),
                            "minWidth"
                        )
                    );
                }

                if (maxWidth && width > maxWidth) {
                    return reject(
                        new ValidationError(
                            format("Error Uploading: Max width is |maxWidth|px", { maxWidth }),
                            "maxWidth"
                        )
                    );
                }

                if (minHeight && height < minHeight) {
                    return reject(
                        new ValidationError(
                            format("Error Uploading: Minimum height is |minHeight|px", { minHeight }),
                            "minHeight"
                        )
                    );
                }

                if (maxHeight && height > maxHeight) {
                    return reject(
                        new ValidationError(
                            format("Error Uploading: Max height is |maxHeight|px", { maxHeight }),
                            "maxHeight"
                        )
                    );
                }

                if (aspectRatio === "square" && height !== width) {
                    return reject(new ValidationError(format("Image must have the same width and height"), "ratio"));
                }

                if (
                    aspectRatio === "custom" &&
                    aspectRatioH &&
                    aspectRatioV &&
                    Math.round((aspectRatioH / aspectRatioV) * height) !== width
                ) {
                    return reject(
                        new ValidationError(
                            format("Image must have a |aspectRatioH|:|aspectRatioV| aspect ratio", {
                                aspectRatioH,
                                aspectRatioV,
                            }),
                            "ratio"
                        )
                    );
                }
                return resolve(fileData);
            };
        };
        reader.onerror = (error) => reject(error);
    });
};

export const validateCount = (files: File[], { minFiles, maxFiles }: IValidationProps) => {
    return new Promise((resolve, reject) => {
        if (minFiles && files.length < minFiles) {
            return reject(new ValidationError(format("Minimum files count |count|", { count: minFiles }), "minFiles"));
        }
        if (maxFiles && files.length > maxFiles) {
            return reject(new ValidationError(format("Maximum files count |count|", { count: maxFiles }), "maxFiles"));
        }
        resolve(files);
    });
};

export const withMediaStyles = (override?: CSSProperties) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
        () => ({
            style: {
                position: "relative" as "relative",
                width: "320px",
                height: "250px",
                backgroundColor: theme.palette.disabledButtonColor,
                border: `1px solid ${theme.palette.lightGrey}`,
                borderRadius: "10px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column" as "column",
                alignItems: "center",
                justifyContent: "center",
                ...override,
            },
        }),
        [override]
    );
};

const withBaseStyles = ({ style, activeStyle, rejectStyle }: IProps) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMemo(
        () => ({
            style: {
                position: "relative",
                backgroundColor: theme.palette.lighterGrey,
                ...style,
            },
            activeStyle: {
                border: `1px solid ${theme.palette.primary1Color}`,
                ...activeStyle,
            },
            rejectStyle: {
                backgroundColor: theme.palette.warning,
                ...rejectStyle,
            },
        }),
        [style, activeStyle, rejectStyle]
    );
};
